import React from 'react';
import { ChildProfile } from '../models/childProfile';

interface ChildAvatarProps {
  child: ChildProfile;
  size: number;
  imageUrl?: string | null;
  showBadge?: boolean;
}

const ChildAvatar: React.FC<ChildAvatarProps> = ({ child, size, imageUrl, showBadge = true }) => {
  const colour = child.colour_code || '#6c757d';
  const hasImage = !!imageUrl;

  // Badge sizing: ~40% of avatar, min 18px, hidden below 40px total
  const badgeSize = Math.max(18, Math.round(size * 0.4));
  const badgeFontSize = Math.max(8, Math.round(badgeSize * 0.45));
  const showInitialsBadge = showBadge && hasImage && size >= 40;

  // Border widths scale slightly with size
  const whiteBorder = size >= 80 ? 3 : 2;
  const colourBorder = size >= 80 ? 4 : 3;
  // The inner image circle size (subtract only the coloured border — no gap padding)
  const innerSize = size - colourBorder * 2;

  if (!hasImage) {
    // Fallback: colour-filled circle with centred initials. The inset
    // bottom-darkening + initials text-shadow mirror the .wsg-sdp__circle
    // treatment in the session panel, giving the circle a subtle 3-D feel.
    return (
      <div
        className="child-avatar child-avatar--initials"
        role="img"
        aria-label={`${child.initials} avatar`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: colour,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: Math.max(11, Math.round(size * 0.34)),
          lineHeight: 1,
          boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.18)',
          textShadow: '0 1px 1px rgba(0,0,0,0.25)',
          flexShrink: 0,
        }}
      >
        {child.initials}
      </div>
    );
  }

  // Image avatar with double border and initials badge
  return (
    <div
      className="child-avatar child-avatar--image"
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Outer coloured border ring */}
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          border: `${colourBorder}px solid ${colour}`,
          boxSizing: 'border-box',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        }}
      >
        {/* White border ring via padding, then the image */}
        <div
          style={{
            width: innerSize,
            height: innerSize,
            borderRadius: '50%',
            border: `${whiteBorder}px solid white`,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <img
            src={imageUrl}
            alt={`${child.initials} profile photo`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>
      </div>

      {/* Initials badge — bottom-right */}
      {showInitialsBadge && (
        <div
          className="child-avatar-badge"
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: badgeSize,
            height: badgeSize,
            borderRadius: '50%',
            backgroundColor: colour,
            color: 'white',
            fontWeight: 600,
            fontSize: badgeFontSize,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          {child.initials}
        </div>
      )}
    </div>
  );
};

export default ChildAvatar;
